import { Button, Divider, Group, Stack, Text, Title } from "@mantine/core";
import { IconPrinter } from "@tabler/icons-react";
import { useCallback, useMemo } from "react";
import type { StainSelectionContext } from "../../utils/controlStainSelection";
import { useGenerateCassetteLabels } from "../../hooks";
import { useCassettePrint } from "../../hooks/useCassettePrint";
import type { WorkListStainSubmission } from "../../services/workList.service";
import type { CaseType } from "../../utils/deriveCaseType";
import {
  allowsZeroTechnicalMaterial,
  resolveAccessioningBlockCount,
} from "../../utils/grossingOnlyPolicy";
import type {
  AccessioningPanelOption,
  AccessioningPanelSelection,
} from "../../services/accessioningPanels.service";
import { BulkMarkReceivedCompletedAction } from "../sticker/BulkMarkReceivedCompletedAction";
import SpecimenPreviewCard, {
  type ManualStainsState,
  type StainInfo,
  type StainRemovalTarget,
} from "./SpecimenPreviewCard";

export type {
  ManualStainsState,
  StainInfo,
  StainRemovalTarget,
} from "./SpecimenPreviewCard";
export { areStainInfoOccurrencesEqual } from "./SpecimenPreviewCard";

interface BlocksAndStainsPreviewProps {
  specimens: Array<{
    uiKey?: string;
    id?: string;
    expectedSpecimenNumber?: string;
    number: string;
    sourceId?: string;
    sourceName?: string;
    subsiteId?: string;
    subsiteName?: string;
    collectionMethod?: string;
    material?: string;
    description?: string;
    blockCount?: number;
    committedFindings?: string;
    matchedStainIds?: string[];
  }>;
  slideCompletions: Record<string, Record<string, boolean>>;
  onCompletionChange: (
    specimenIndex: number,
    selectionId: string,
    isCompleted: boolean
  ) => void;
  onBlockCountChange: (specimenIndex: number, count: number) => void;
  caseId?: string;
  onLabelsPrinted?: () => void;
  manualStains?: Record<number, ManualStainsState>;
  onAddStain?: (specimenIndex: number, stain: StainInfo) => void;
  onRemoveStain?: (specimenIndex: number, target: StainRemovalTarget) => void;
  hidePrintButton?: boolean;
  accessioningType?: CaseType;
  practiceId?: string;
  billingScenarioId?: string;
  onResolvedStainsChange?: (specimenIndex: number, stains: StainInfo[]) => void;
  workListStains?: WorkListStainSubmission[];
  assignedPrefix?: string;
  stainSelectionContext?: StainSelectionContext;
  onMarkAllReceivedCompleted?: () => void;
  onClearAllReceivedCompleted?: () => void;
  panelOptions?: AccessioningPanelOption[];
  panelSelections?: Record<number, AccessioningPanelSelection[]>;
  panelsLoading?: boolean;
  onAddPanelSelection?: (
    specimenIndex: number,
    option: AccessioningPanelOption
  ) => void;
  onRemovePanelSelection?: (
    specimenIndex: number,
    selectionKey: string
  ) => void;
}

export default function BlocksAndStainsPreview({
  specimens,
  slideCompletions,
  onCompletionChange,
  onBlockCountChange,
  caseId,
  onLabelsPrinted,
  manualStains,
  onAddStain,
  onRemoveStain,
  hidePrintButton = false,
  accessioningType,
  practiceId,
  billingScenarioId,
  onResolvedStainsChange,
  workListStains,
  assignedPrefix,
  stainSelectionContext,
  onMarkAllReceivedCompleted,
  onClearAllReceivedCompleted,
  panelOptions,
  panelSelections,
  panelsLoading,
  onAddPanelSelection,
  onRemovePanelSelection,
}: BlocksAndStainsPreviewProps) {
  const generateLabelsMutation = useGenerateCassetteLabels();
  const { printLabels } = useCassettePrint();
  const specimensWithSource = useMemo(
    () => specimens.filter((specimen) => specimen.sourceId),
    [specimens]
  );
  const totalBlocks = useMemo(
    () =>
      specimensWithSource.reduce(
        (sum, specimen) =>
          sum +
          resolveAccessioningBlockCount(accessioningType, specimen.blockCount),
        0
      ),
    [accessioningType, specimensWithSource]
  );
  const workListStainsBySpecimen = useMemo(
    () =>
      (workListStains ?? []).reduce<Record<string, WorkListStainSubmission[]>>(
        (bySpecimen, stain) => {
          const current = bySpecimen[stain.specimenNumber] ?? [];
          bySpecimen[stain.specimenNumber] = [...current, stain];
          return bySpecimen;
        },
        {}
      ),
    [workListStains]
  );

  const handlePrintCassettes = useCallback(async () => {
    if (!caseId) return;
    try {
      const labels = await generateLabelsMutation.mutateAsync({
        caseId,
        request: {
          specimens: specimensWithSource.map((specimen) => ({
            specimenLetter: specimen.number,
            blockCount: resolveAccessioningBlockCount(
              accessioningType,
              specimen.blockCount
            ),
          })),
        },
      });
      const printed = await printLabels(labels);
      if (printed) onLabelsPrinted?.();
    } catch {
      // The mutation hook reports the user-facing error.
    }
  }, [
    caseId,
    generateLabelsMutation,
    onLabelsPrinted,
    printLabels,
    specimensWithSource,
    accessioningType,
  ]);

  if (specimensWithSource.length === 0) return null;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="center">
        <Title order={5}>Blocks & Slides Preview</Title>
        {!allowsZeroTechnicalMaterial(accessioningType) &&
          accessioningType !== "global" &&
          onMarkAllReceivedCompleted &&
          onClearAllReceivedCompleted && (
            <BulkMarkReceivedCompletedAction
              onMarkAll={onMarkAllReceivedCompleted}
              onClearAll={onClearAllReceivedCompleted}
            />
          )}
      </Group>
      <Text size="xs" c="dimmed">
        {allowsZeroTechnicalMaterial(accessioningType)
          ? "No blocks or slides will be created for this workflow"
          : accessioningType === "tc_only"
          ? "Received blocks and slides to be created at cutting"
          : "Review blocks and slides that will be created"}
      </Text>

      <Stack gap="sm">
        {specimens.map((specimen, specimenIndex) =>
          specimen.sourceId ? (
            <SpecimenPreviewCard
              key={
                specimen.uiKey ||
                specimen.id ||
                specimen.expectedSpecimenNumber ||
                specimen.number
              }
              specimen={specimen}
              specimenIndex={specimenIndex}
              slideCompletions={slideCompletions[specimenIndex] || {}}
              onCompletionChange={onCompletionChange}
              onBlockCountChange={onBlockCountChange}
              manualStains={manualStains?.[specimenIndex]}
              onAddStain={onAddStain}
              onRemoveStain={onRemoveStain}
              accessioningType={accessioningType}
              practiceId={practiceId}
              billingScenarioId={billingScenarioId}
              onResolvedStainsChange={onResolvedStainsChange}
              assignedPrefix={assignedPrefix}
              stainSelectionContext={stainSelectionContext}
              workListStainsForSpecimen={
                workListStainsBySpecimen[specimen.number]
              }
              panelOptions={
                allowsZeroTechnicalMaterial(accessioningType)
                  ? []
                  : panelOptions
              }
              panelSelections={
                allowsZeroTechnicalMaterial(accessioningType)
                  ? []
                  : panelSelections?.[specimenIndex]
              }
              panelsLoading={
                allowsZeroTechnicalMaterial(accessioningType)
                  ? false
                  : panelsLoading
              }
              onAddPanelSelection={
                allowsZeroTechnicalMaterial(accessioningType)
                  ? undefined
                  : onAddPanelSelection
              }
              onRemovePanelSelection={
                allowsZeroTechnicalMaterial(accessioningType)
                  ? undefined
                  : onRemovePanelSelection
              }
            />
          ) : null
        )}
      </Stack>

      {!hidePrintButton && caseId && totalBlocks > 0 && (
        <>
          <Divider />
          <Button
            leftSection={<IconPrinter size={16} />}
            onClick={handlePrintCassettes}
            loading={generateLabelsMutation.isPending}
            variant="light"
            fullWidth
          >
            Print Cassettes ({totalBlocks})
          </Button>
        </>
      )}
    </Stack>
  );
}
